import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { getVisualizationData, PlanData, updateVisualizationState } from '@/api/custom';
import './index.scss';

const MOBILE_BREAKPOINT = 768;

const formatDate = (timestamp: number) => dayjs(timestamp * 1000).format('MMM D, YYYY');

// Untitled plans are named from their file, "<id>_<name>_…".
const planTitle = (plan: PlanData) =>
  plan.title && plan.title !== 'Untitled'
    ? plan.title
    : plan.visualizationHtml?.fileName?.split('_')[1] || 'Financial plan';

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

/**
 * Financial Plan tab of the investor record (boards RecordPlan, MPlanning):
 * plan history on the left, the selected plan's advisor visualization on the
 * right. The visualization is the advisor's own HTML, so its questions are
 * answered inside the frame and saved from here.
 */
const Planning: React.FC = () => {
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Bumping this remounts the frame, which drops unsaved answers.
  const [frameVersion, setFrameVersion] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getVisualizationData();
        const sorted = Array.isArray(response)
          ? [...response].sort((a, b) => b.sharedAt - a.sharedAt)
          : [];
        setPlans(sorted);
        setSelectedId(sorted[0]?.id ?? null);
      } catch (err) {
        console.error('Error fetching visualization data:', err);
        setError("We couldn't load your financial plans. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, []);

  const selectedPlan = plans.find((plan) => plan.id === selectedId) ?? plans[0];
  const sourceCode = selectedPlan?.visualizationHtml?.sourceCode || '';

  // The frame loads the advisor's defaults; the investor's saved answers go
  // back in on every load, so Discard returns to them, not to the defaults.
  const applySavedAnswers = (doc: Document, answers: Record<string, unknown> | undefined) => {
    Object.entries(answers ?? {}).forEach(([id, value]) => {
      const element = doc.getElementById(id);
      if (element?.tagName !== 'INPUT') return;
      const input = element as HTMLInputElement;
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = Boolean(value);
      } else {
        input.value = String(value ?? '');
      }
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    try {
      const doc = iframe.contentDocument;
      if (doc) {
        applySavedAnswers(doc, selectedPlan?.userAnswers);
      }
      iframe.contentWindow.postMessage('initialize', '*');
      const frameWindow = iframe.contentWindow as Window & { updateChart?: () => void };
      frameWindow.updateChart?.();
      const body = (iframe.contentDocument || frameWindow.document)?.body;
      if (body) {
        iframe.style.height = `${body.scrollHeight}px`;
      }
    } catch (e) {
      console.error('chart initialization failed:', e);
    }
  };

  const selectPlan = (id: string) => {
    // Re-picking the open plan would remount the frame and drop unsaved answers.
    if (id === selectedPlan?.id) return;
    setSelectedId(id);
    setFrameVersion((version) => version + 1);
  };

  const saveAnswers = async () => {
    const iframeDoc = iframeRef.current?.contentDocument;
    if (!iframeDoc || !selectedPlan) return;

    const inputValues: Record<string, string | boolean> = {};
    iframeDoc.querySelectorAll('input').forEach((input) => {
      inputValues[input.id] =
        input.type === 'checkbox' || input.type === 'radio' ? input.checked : input.value;
    });

    setSaving(true);
    try {
      await updateVisualizationState(selectedPlan.id, inputValues);
      // These are now the answers Discard and a reload come back to.
      setPlans((current) =>
        current.map((plan) =>
          plan.id === selectedPlan.id ? { ...plan, userAnswers: inputValues } : plan,
        ),
      );
      message.success('Answers saved. Your advisor will see them.');
    } catch {
      message.error("Couldn't save your answers. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="record-plan">
        <div className="record-plan-state">
          <Spin />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="record-plan">
        <Alert type="error" showIcon message={error} />
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="record-plan">
        <p className="record-plan-state record-plan-card">
          Your advisor hasn't shared a financial plan yet. When they do, it will show here.
        </p>
      </div>
    );
  }

  const keyword = search.trim().toLowerCase();
  const listedPlans = keyword
    ? plans.filter((plan) => planTitle(plan).toLowerCase().includes(keyword))
    : plans;

  return (
    <div className="record-plan">
      {isMobile ? (
        <label className="record-plan-field">
          Plan
          <select value={selectedPlan.id} onChange={(event) => selectPlan(event.target.value)}>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {planTitle(plan)} · {formatDate(plan.sharedAt)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <section className="record-plan-history record-plan-card" aria-label="Plan history">
          <div className="record-plan-eyebrow">Plan history</div>
          <label className="record-plan-search">
            <SearchIcon />
            <span className="record-plan-sr">Search plans</span>
            <input
              type="search"
              placeholder="Search plans"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {listedPlans.map((plan) => (
            <button
              key={plan.id}
              type="button"
              className={`record-plan-item${plan.id === selectedPlan.id ? ' is-active' : ''}`}
              aria-current={plan.id === selectedPlan.id ? 'true' : undefined}
              onClick={() => selectPlan(plan.id)}
            >
              <span className="record-plan-item-name">{planTitle(plan)}</span>
              <span className="record-plan-item-date">Shared {formatDate(plan.sharedAt)}</span>
            </button>
          ))}
          {listedPlans.length === 0 && <p className="record-plan-none">No plans match.</p>}
        </section>
      )}

      <section className="record-plan-detail record-plan-card" aria-label={planTitle(selectedPlan)}>
        <header className="record-plan-detail-head">
          <h2>{planTitle(selectedPlan)}</h2>
          <div className="record-plan-meta">Shared {formatDate(selectedPlan.sharedAt)}</div>
        </header>

        <figure className="record-plan-frame">
          <figcaption>
            <span className="record-plan-eyebrow">Advisor visualization</span>
            <span>Rendered from the plan your advisor built</span>
          </figcaption>
          {sourceCode ? (
            <iframe
              key={`${selectedPlan.id}-${frameVersion}`}
              ref={iframeRef}
              className="record-plan-iframe"
              title={`${planTitle(selectedPlan)} visualization`}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              srcDoc={sourceCode}
              onLoad={handleIframeLoad}
            />
          ) : (
            <p className="record-plan-none">This plan has no visualization to show.</p>
          )}
        </figure>

        {sourceCode && (
          <footer className="record-plan-answers">
            <div>
              <h3>Your answers</h3>
              <p>Answer the plan's questions above. Your advisor uses them to update the plan.</p>
            </div>
            <div className="record-plan-actions">
              <Button disabled={saving} onClick={() => setFrameVersion((version) => version + 1)}>
                Discard changes
              </Button>
              <Button type="primary" loading={saving} onClick={() => void saveAnswers()}>
                Save answers
              </Button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
};

export default Planning;
